// Vue
import { mixins } from "vue-class-component";
import { Vue, Component, Prop, Watch } from "vue-property-decorator";

// Template
import template from "./template.vue";

// Model
import { AttachmentDTO } from "../../../models/attachment/AttachmentDTO";

// Mixins
import { FormInput } from "./../../../mixins/form_input/FormInput";

// Components
import { Button } from "../button/Button";
import { Icon } from "../icon/Icon";
import { Input } from "../input/Input";
import { Link } from "../link/Link";
import { H } from "../h/H";
import { Message } from "../message/Message";
import { Modal } from "../modal/Modal";
import { getIconFromFileType } from "../../../services/FileTypeToIconService";

enum FileError {
  SIZE = "SIZE_ERROR",
  FORMAT = "FORMAT_ERROR"
}

@Component({
  name: "FileInput",
  components: {
    Button,
    Icon,
    Input,
    Link,
    H,
    Message,
    Modal
  },
  mixins: [template]
})
export class FileInput extends mixins(FormInput) {
  public getFileDescription(f: AttachmentDTO) {
    let size = f.size;
    let measure = "B";
    if (size > 1000) {
      size = size / 1024;
      measure = "KB";
    }
    if (size > 1000) {
      size = size / 1024;
      measure = "MB";
    }
    if (size > 1000) {
      size = size / 1024;
      measure = "GB";
    }

    return (
      f.name +
      " (" +
      f.create_date +
      " - " +
      size.toLocaleString("de-DE") +
      " " +
      measure +
      ")"
    );
  }

  public get maxSizeMessage() {
    return (this.maxSize / 1024 / 1024).toLocaleString("de-DE");
  }

  public get allowedFormatsMessage() {
    return this.allowedFormatsNames.join(", ");
  }

  public get filteredErrors() {
    return this.displayErrors.filter(
      error => error != "" && error != undefined && error != null
    );
  }

  public get errorClass() {
    return this.$slots["validation"] || this.filteredErrors.length > 0
      ? `${this.$appPrefix}-file-input--has-error`
      : "";
  }

  public get emitValue() {
    if (this.multiple) {
      return this.selectedFiles.map(fileInfo => fileInfo.file);
    } else {
      if (this.value instanceof Array) {
        return this.selectedFiles.length > 0
          ? [this.selectedFiles[0].file]
          : [];
      } else {
        return this.selectedFiles.length > 0
          ? this.selectedFiles[0].file
          : null;
      }
    }
  }

  public classes: string[] = [];

  public fileErrorEnum = FileError;

  public isDragIn: boolean = false;
  public selectedFiles: {
    file: File;
    editName: string;
    editMode: boolean;
    imgPreview?: string;
  }[] = [];
  public errorFiles: { file: File; error: FileError }[] = [];
  public fileTypeMap: object = {
    "image/jpeg": "JPEG",
    "image/jpg": "JPEG",
    "image/png": "PNG",
    "application/pdf": "PDF"
  };

  public savedFileForDelete?: AttachmentDTO;
  public isVisibleCheckdeleteSavedFile: boolean = false;

  @Prop({ type: [Array, File] })
  public value!: File | File[];

  @Prop({ default: false })
  public multiple!: boolean;

  @Prop({ default: () => [] })
  public displayErrors!: string[];

  @Prop({ default: true })
  public isUploadVisible!: boolean;

  @Prop({ default: true })
  public showErrors!: boolean;

  @Prop({ default: () => [] })
  public savedFiles!: AttachmentDTO[];

  @Prop({ default: true })
  public isSavedFilesDeletable!: boolean;

  @Prop({ default: () => [] })
  public allowedFormats!: string[];

  @Prop({ default: () => [] })
  public allowedFormatsNames!: string[];

  @Prop()
  public maxSize!: number;

  @Prop()
  public formatErrorMsg!: string;

  @Prop()
  public sizeErrorMsg!: string;

  @Prop()
  public id!: string;

  @Prop()
  public name!: string;

  @Prop({default: "0"})
  public tabindex!: string;

  @Prop({ default: false })
  public disabled!: boolean;

  @Prop({ default: false })
  public readMode!: boolean;

  @Prop({ default: false })
  public override!: boolean;

  @Prop({ default: true })
  public changeFilename!: boolean;

  @Watch("value")
  private watchValue(value, oldValue) {
    if (!value || (value instanceof Array && value.length == 0)) {
      this.selectedFiles = [];
    }

    // this.handleValueChanges(value)
  }

  @Watch("selectedFiles")
  private watchSelectedFiles() {
    // This is wrapped in a try-catch-block because it may not work in every browser.
    try {
      const dT = new DataTransfer();
      this.selectedFiles.forEach(x => {
        dT.items.add(x.file);
      });
      // @ts-ignore
      this.$refs.input.files = dT.files;
    } catch (e) {
    }
  }

  public created() {}

  public handleFileDrop(e: DragEvent) {
    this.isDragIn = false;
    if (e.dataTransfer) {
      this.handleFileList(e.dataTransfer.files);
    }
  }

  public handleFileSelect(e: Event) {
    const input = <HTMLInputElement>e.target;
    this.handleFileList(input.files);
    // input.value = ""; // Not working in abh_profile apps
  }

  public handleFileList(files: FileList | null) {
    if (files) {
      if (this.override) {
        this.selectedFiles = [];
      }

      for (let i = 0; i < files.length; i++) {
        if (!this.multiple && i > 0) return;

        const file = files.item(i);

        if (file) {
          const error = this.hasFileErrors(file);

          if (error) {
            this.errorFiles.push({ file, error });
            continue;
          }

          if (
            file.type == "image/jpeg" ||
            file.type == "image/jpg" ||
            file.type == "image/png"
          ) {
            const reader = new FileReader();

            reader.onload = () => {
              const imgPreview = <string>reader.result;

              if (!this.multiple) this.selectedFiles = [];
              this.selectedFiles.push({
                file,
                imgPreview,
                editName: file.name,
                editMode: false
              });
              this.$emit("input", this.emitValue);
            };

            reader.readAsDataURL(file);
          } else {
            if (!this.multiple) this.selectedFiles = [];
            this.selectedFiles.push({
              file,
              editName: file.name,
              editMode: false
            });

            this.$emit("input", this.emitValue);
          }
        }
      }
    }
  }

  public handleValueChanges(value?: File | File[]) {
    this.selectedFiles = [];

    if (value instanceof Array) {
      for (let i = 0; i < value.length; i++) {
        if (!this.multiple && i > 0) return;

        const file = value[i];

        if (file instanceof File) {
          if (
            file.type == "image/jpeg" ||
            file.type == "image/jpg" ||
            file.type == "image/png"
          ) {
            const reader = new FileReader();

            reader.onload = () => {
              const imgPreview = <string>reader.result;

              this.selectedFiles.push({
                file,
                imgPreview,
                editName: file.name,
                editMode: false
              });
            };

            reader.readAsDataURL(file);
          } else {
            this.selectedFiles.push({
              file,
              editName: file.name,
              editMode: false
            });
          }
        }
      }
    } else if (value instanceof File) {
      const file = value;

      if (
        file.type == "image/jpeg" ||
        file.type == "image/jpg" ||
        file.type == "image/png"
      ) {
        const reader = new FileReader();

        reader.onload = () => {
          const imgPreview = <string>reader.result;

          this.selectedFiles.push({
            file,
            imgPreview,
            editName: file.name,
            editMode: false
          });
        };

        reader.readAsDataURL(file);
      } else {
        this.selectedFiles.push({ file, editName: file.name, editMode: false });
      }
    }
  }

  // Actions

  public editFileName(fileIndex: number) {
    const fileInfo = this.selectedFiles[fileIndex];

    if (fileInfo) {
      fileInfo.editMode = true;
      fileInfo.editName = fileInfo.file.name;
    }
  }

  public saveFileName(fileIndex: number) {
    if (
      !/^[a-zA-Z0-9\-_]+(\.[a-z]+)?$/gi.test(
        this.selectedFiles[fileIndex].editName
      )
    )
      return;

    const fileInfo = this.selectedFiles[fileIndex];

    if (fileInfo) {
      fileInfo.editMode = false;

      let newFile;
      try {
        newFile = new File([fileInfo.file], `${fileInfo.editName}`, {
          type: fileInfo.file.type
        })
      } catch (err) {
        // `new File` is not supported in IE11 and the old Edge
        // therefore we use Blob here
        let blob = new Blob([fileInfo.file], {type: fileInfo.file.type});
        // @ts-ignore
        blob.name = `${fileInfo.editName}`;

        newFile = blob;
      }
      fileInfo.file = newFile;

      this.$emit("input", this.emitValue);
    }
  }

  public cancelEditFileName(fileIndex: number) {
    const fileInfo = this.selectedFiles[fileIndex];

    if (fileInfo) {
      fileInfo.editMode = false;
      fileInfo.editName = fileInfo.file.name;
    }
  }

  public deleteFile(fileIndex: number) {
    if (this.selectedFiles[fileIndex]) {
      this.selectedFiles.splice(fileIndex, 1);
    }

    if (!this.multiple) {
      (this.$refs['input'] as HTMLInputElement).value = '';
    }

    this.$emit("input", this.emitValue);
  }

  public handleCheckDeleteSavedFile(file: AttachmentDTO) {
    this.savedFileForDelete = file;
    this.isVisibleCheckdeleteSavedFile = true;
  }

  public deleteSavedFile(shouldDelete: boolean) {
    this.isVisibleCheckdeleteSavedFile = false;

    if (shouldDelete) {
      this.$emit("delete-saved", this.savedFileForDelete);
    }
  }

  public hasFileErrors(file: File) {
    if (
      this.allowedFormats &&
      this.allowedFormats.length &&
      !this.allowedFormats.includes(file.type)
    ) {
      return FileError.FORMAT;
    } else if (this.maxSize && this.maxSize < file.size) {
      return FileError.SIZE;
    } else {
      return false;
    }
  }

  public handleCloseErrorsPopup() {
    this.errorFiles = [];
  }

  protected getMimeTypeIcon(mimeType: string) {
    return getIconFromFileType(mimeType);
  }
}
